'use client';
import { useState, useRef } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import Link from 'next/link';
import { ArrowLeft, Copy, Download, Check, FileUp, Columns } from 'lucide-react';

export default function ColumnSplitterPage() {
  const [csvInput, setCsvInput] = useState('');
  const [csvOutput, setCsvOutput] = useState('');
  const [delimiter, setDelimiter] = useState(' ');
  const [columnIndex, setColumnIndex] = useState('0');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const splitColumn = () => {
    try {
      setError('');
      if (!csvInput.trim()) {
        setError('Please enter CSV data');
        return;
      }

      const lines = csvInput.trim().split('\n');
      if (lines.length < 1) {
        setError('CSV is empty');
        return;
      }

      const colIdx = parseInt(columnIndex);
      const result = lines.map(line => {
        const columns = line.split(',');
        if (colIdx >= columns.length) return line;

        const target = columns[colIdx];
        const parts = target.split(delimiter);
        
        // Replace the original column with the split parts
        const newCols = [...columns];
        newCols.splice(colIdx, 1, ...parts);
        return newCols.join(',');
      });

      setCsvOutput(result.join('\n'));
    } catch (err) {
      setError('Error splitting column. Please check your input format.');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(csvOutput);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadCsv = () => {
    const blob = new Blob([csvOutput], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'split_data.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (file: File) => {
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.txt')) {
      setError('Please upload a CSV or TXT file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setCsvInput(text);
    };
    reader.onerror = () => {
      setError('Error reading file');
    };
    reader.readAsText(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="marketing-theme tool-page">

      <Navbar />

      <main className="tool-page-main flex flex-col items-center">
        <div className="w-full max-w-5xl">
          <Link 
            href="/tools" 
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tools
          </Link>

          <div className="text-center mb-8">
              <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                CSV Column Splitter
              </h1>
            <p className="text-lg text-muted-foreground">
              Split a single column into multiple columns based on a delimiter
            </p>
          </div>

          {/* File Upload Area */}
          <div className="mb-6">
            <label className="text-sm font-medium mb-2 block">Upload CSV File</label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={triggerFileInput}
              className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-all ${
                isDragging 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                  : 'border-gray-300 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-600'
              }`}
            >
              <FileUp className={`w-12 h-12 mx-auto mb-4 ${
                isDragging ? 'text-blue-500' : 'text-gray-400'
              }`} />
              <p className="text-lg font-medium mb-2">
                {isDragging ? 'Drop your file here' : 'Click to upload or drag and drop'}
              </p>
              <p className="text-sm text-muted-foreground">
                Supports .csv and .txt files
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                onChange={handleFileInputChange}
                className="hidden"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="space-y-2">
              <label className="text-sm font-medium">Column Index (0-based)</label>
              <Input 
                type="number" 
                value={columnIndex} 
                onChange={(e) => setColumnIndex(e.target.value)}
                placeholder="e.g. 0 for first column"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Split Delimiter</label>
              <Input 
                value={delimiter} 
                onChange={(e) => setDelimiter(e.target.value)}
                placeholder="e.g. space, dash, etc."
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Section */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">CSV Input</label>
                <Textarea
                  placeholder="Full Name,Age&#10;John Doe,30&#10;Jane Smith,25"
                  value={csvInput}
                  onChange={(e) => setCsvInput(e.target.value)}
                  className="min-h-[200px] font-mono text-sm"
                />
              </div>
              <Button 
                onClick={splitColumn} 
                className="w-full"
                size="lg"
              >
                <Columns className="w-4 h-4 mr-2" />
                Split Column
              </Button>
              {error && (
                <div className="text-red-500 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                  {error}
                </div>
              )}
            </div>

            {/* Output Section */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">CSV Output</label>
                <Textarea
                  value={csvOutput}
                  readOnly
                  placeholder="Your split data will appear here..."
                  className="min-h-[200px] font-mono text-sm bg-muted"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={copyToClipboard} 
                  className="flex-1"
                  variant="outline"
                  disabled={!csvOutput}
                >
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
                <Button 
                  onClick={downloadCsv} 
                  className="flex-1"
                  variant="outline"
                  disabled={!csvOutput}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <section className="max-w-4xl mx-auto mt-16 mb-12 p-6 bg-card rounded-lg border border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold mb-4">Column Splitter FAQs</h2>
        <div className="space-y-6">
          <div><p className="font-semibold">1. What is a 0-based index?</p><p className="text-muted-foreground">It means the first column is 0, the second is 1, and so on.</p></div>
          <div><p className="font-semibold">2. Can I split by multiple characters?</p><p className="text-muted-foreground">Yes, the delimiter can be any string of characters.</p></div>
          <div><p className="font-semibold">3. What happens if the column doesn't contain the delimiter?</p><p className="text-muted-foreground">The column will remain unchanged in the output.</p></div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
